import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

/*
 * The governance board, server side.
 *
 * ADR-006 moved this out of Notion. What it replaced was three of the six
 * Notion databases; the other three were deliberately not migrated, because
 * `team_members` already holds People and the repository is its own Doc Index.
 *
 * A decision row carries a status and a path, never a body. The reasoning
 * lives in `docs/decisions/ADR-NNN.md` and duplicating it here would guarantee
 * the row and the file eventually disagree.
 */

export type FindingKind = 'security' | 'architecture' | 'risk' | 'question' | 'doc-drift' | 'other';
export type Severity = 'p1' | 'p2' | 'p3';
export type FindingStatus = 'open' | 'triaged' | 'in-progress' | 'resolved' | 'wont-fix';

export interface Finding {
  id: string;
  ref: string;
  title: string;
  detail: string | null;
  kind: FindingKind;
  severity: Severity;
  status: FindingStatus;
  location: string | null;
  resolved_when: string | null;
  resolved_at: string | null;
  updated_at: string;
}

export type DecisionStatus = 'proposed' | 'accepted' | 'superseded' | 'rejected';

export interface Decision {
  id: string;
  ref: string;
  title: string;
  doc_path: string | null;
  status: DecisionStatus;
  superseded_by: string | null;
  decided_on: string | null;
}

export type MilestoneStatus = 'planned' | 'active' | 'done' | 'dropped';

export interface Milestone {
  id: string;
  ref: string;
  title: string;
  detail: string | null;
  status: MilestoneStatus;
  target_date: string | null;
  sort_order: number;
}

export const findingsKey = (orgId: string | null) => ['governance-findings', orgId] as const;
export const decisionsKey = (orgId: string | null) => ['governance-decisions', orgId] as const;
export const milestonesKey = (orgId: string | null) => ['governance-milestones', orgId] as const;

export function useFindings(orgId: string | null) {
  return useQuery({
    queryKey: findingsKey(orgId),
    queryFn: async (): Promise<Finding[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('governance_findings')
        .select('id, ref, title, detail, kind, severity, status, location, resolved_when, resolved_at, updated_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('severity', { ascending: true })
        .order('ref', { ascending: true });
      if (error) throw error;
      return data as unknown as Finding[];
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

export function useDecisions(orgId: string | null) {
  return useQuery({
    queryKey: decisionsKey(orgId),
    queryFn: async (): Promise<Decision[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('governance_decisions')
        .select('id, ref, title, doc_path, status, superseded_by, decided_on')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('decided_on', { ascending: false });
      if (error) throw error;
      return data as unknown as Decision[];
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useMilestones(orgId: string | null) {
  return useQuery({
    queryKey: milestonesKey(orgId),
    queryFn: async (): Promise<Milestone[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('governance_milestones')
        .select('id, ref, title, detail, status, target_date, sort_order')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data as unknown as Milestone[];
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export interface FindingInput {
  id: string | null;
  ref: string;
  title: string;
  detail: string | null;
  kind: FindingKind;
  severity: Severity;
  status: FindingStatus;
  location: string | null;
  resolved_when: string | null;
  userId: string | null;
}

export function useSaveFinding(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FindingInput) => {
      if (!orgId) throw new Error('אין ארגון');
      const closed = input.status === 'resolved' || input.status === 'wont-fix';
      const row = {
        organization_id: orgId,
        ref: input.ref.trim(),
        title: input.title.trim(),
        detail: input.detail,
        kind: input.kind,
        severity: input.severity,
        status: input.status,
        location: input.location,
        resolved_when: input.resolved_when,
        // Stamped from the status rather than asked for separately: a finding
        // marked resolved with no resolved_at is the kind of half-state that
        // makes a board untrustworthy.
        resolved_at: closed ? new Date().toISOString() : null,
        created_by: input.userId,
      };
      const query =
        input.id === null
          ? supabase.from('governance_findings').insert(row)
          : supabase.from('governance_findings').update(row).eq('id', input.id);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: findingsKey(orgId) });
    },
  });
}

/** The next free F-NNNN for this organization. */
export function nextFindingRef(findings: Finding[]): string {
  const highest = findings.reduce((max, f) => {
    const n = Number(f.ref.slice(2));
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `F-${String(highest + 1).padStart(4, '0')}`;
}
